import { bahtToMinor, formatMinor } from '@natashu/shared';
import { Check, ChevronDown, CircleAlert, Eye, EyeOff, Minus, Search, X, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ComponentPropsWithRef, type ReactNode, type Ref } from 'react';
import { cx } from '../../lib/cx.ts';
import { useDebouncedValue, useLatest } from '../../lib/hooks.ts';
import './Field.css';

interface FieldBaseProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Keep the label for screen readers only (e.g. search boxes). */
  labelHidden?: boolean;
  containerClassName?: string;
}

function describedBy(id: string, hint: ReactNode, error: ReactNode, extra?: string): string | undefined {
  const ids = [error ? `${id}-error` : hint ? `${id}-hint` : '', extra ?? ''].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

export interface FieldShellProps extends FieldBaseProps {
  id: string;
  required?: boolean;
  children: ReactNode;
}

/** Label above (7px gap), control, then hint or error below (DESIGN.md §9 Form Controls). */
export function FieldShell({ id, label, hint, error, required, labelHidden, containerClassName, children }: FieldShellProps) {
  return (
    <div className={cx('field', error ? 'has-error' : null, containerClassName)}>
      {label ? (
        <label htmlFor={id} className={cx('field__label', labelHidden && 'visually-hidden')}>
          {label}
          {required && (
            <span className="field__required" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="field__error">
          <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends Omit<ComponentPropsWithRef<'input'>, 'size'>, FieldBaseProps {
  icon?: LucideIcon;
  /** Short text prefix such as "฿". */
  leading?: ReactNode;
  trailing?: ReactNode;
}

export function TextInput({
  label,
  hint,
  error,
  labelHidden,
  containerClassName,
  icon: Icon,
  leading,
  trailing,
  className,
  id: idProp,
  required,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextInputProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} labelHidden={labelHidden} containerClassName={containerClassName}>
      <div className={cx('input-wrap', Icon || leading ? 'has-leading' : null, trailing ? 'has-trailing' : null)}>
        {Icon ? (
          <Icon className="input-wrap__leading" size={18} strokeWidth={1.75} aria-hidden="true" />
        ) : leading ? (
          <span className="input-wrap__leading input-wrap__affix" aria-hidden="true">
            {leading}
          </span>
        ) : null}
        <input
          {...rest}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error, ariaDescribedBy)}
          className={cx('input', className)}
        />
        {trailing ? <div className="input-wrap__trailing">{trailing}</div> : null}
      </div>
    </FieldShell>
  );
}

export function PasswordInput(props: Omit<TextInputProps, 'type' | 'trailing'>) {
  const [visible, setVisible] = useState(false);
  return (
    <TextInput
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      {...props}
      type={visible ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          className="input-action"
          aria-label={visible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" /> : <Eye size={18} strokeWidth={1.75} aria-hidden="true" />}
        </button>
      }
    />
  );
}

export interface TextareaProps extends ComponentPropsWithRef<'textarea'>, FieldBaseProps {}

export function Textarea({ label, hint, error, labelHidden, containerClassName, className, id: idProp, required, 'aria-describedby': ariaDescribedBy, ...rest }: TextareaProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} labelHidden={labelHidden} containerClassName={containerClassName}>
      <textarea
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error, ariaDescribedBy)}
        className={cx('input', 'textarea', className)}
      />
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentPropsWithRef<'select'>, 'children'>, FieldBaseProps {
  options: readonly SelectOption[];
  placeholder?: string;
}

export function Select({ label, hint, error, labelHidden, containerClassName, className, id: idProp, required, options, placeholder, 'aria-describedby': ariaDescribedBy, ...rest }: SelectProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} labelHidden={labelHidden} containerClassName={containerClassName}>
      <div className="input-wrap has-trailing select-wrap">
        <select
          {...rest}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error, ariaDescribedBy)}
          className={cx('input', 'select', className)}
        >
          {placeholder !== undefined && (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="select-chevron" size={16} strokeWidth={1.75} aria-hidden="true" />
      </div>
    </FieldShell>
  );
}

export interface CheckboxProps extends Omit<ComponentPropsWithRef<'input'>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
}

export function Checkbox({ label, description, indeterminate = false, className, ref, id: idProp, ...rest }: CheckboxProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const inner = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label htmlFor={id} className={cx('check', rest.disabled && 'is-disabled', className)}>
      <input
        {...rest}
        ref={(el) => {
          inner.current = el;
          assignRef(ref, el);
        }}
        id={id}
        type="checkbox"
        className="check__input"
        aria-describedby={description ? `${id}-desc` : undefined}
      />
      <span className="check__box" aria-hidden="true">
        {indeterminate ? <Minus size={14} strokeWidth={2.5} /> : <Check size={14} strokeWidth={2.5} />}
      </span>
      <span className="check__text">
        <span className="check__label">{label}</span>
        {description && (
          <span id={`${id}-desc`} className="check__desc">
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function Switch({ checked, onChange, label, description, disabled = false, id: idProp, className }: SwitchProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <div className={cx('switch-row', disabled && 'is-disabled', className)}>
      <div className="switch-row__text">
        <label htmlFor={id} id={`${id}-label`} className="switch-row__label">
          {label}
        </label>
        {description && (
          <span id={`${id}-desc`} className="switch-row__desc">
            {description}
          </span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-desc` : undefined}
        disabled={disabled}
        className="switch"
        onClick={() => onChange(!checked)}
      >
        <span className="switch__thumb" />
      </button>
    </div>
  );
}

export interface SearchInputProps extends Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'type' | 'icon' | 'trailing'> {
  value?: string;
  defaultValue?: string;
  /** Fires on every keystroke. */
  onChange?: (value: string) => void;
  /** Fires after typing pauses for `debounceMs`, on Enter and on clear. */
  onSearch?: (value: string) => void;
  debounceMs?: number;
}

export function SearchInput({
  value,
  defaultValue = '',
  onChange,
  onSearch,
  debounceMs = 300,
  label = 'ค้นหา',
  labelHidden = true,
  placeholder = 'ค้นหา…',
  onKeyDown,
  ...rest
}: SearchInputProps) {
  const [text, setText] = useState(value ?? defaultValue);
  const debounced = useDebouncedValue(text, debounceMs);
  const onSearchRef = useLatest(onSearch);
  const lastEmitted = useRef(text);

  useEffect(() => {
    if (value !== undefined) setText(value);
  }, [value]);

  const emit = (v: string) => {
    if (v === lastEmitted.current) return;
    lastEmitted.current = v;
    onSearchRef.current?.(v);
  };

  useEffect(() => {
    emit(debounced);
    // `emit` only reads refs, so the debounced value is the only trigger.
  }, [debounced]);

  const update = (v: string) => {
    setText(v);
    onChange?.(v);
  };

  return (
    <TextInput
      {...rest}
      type="search"
      icon={Search}
      label={label}
      labelHidden={labelHidden}
      placeholder={placeholder}
      enterKeyHint="search"
      autoComplete="off"
      value={text}
      onChange={(e) => update(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') emit(text);
        if (e.key === 'Escape' && text) {
          e.preventDefault();
          update('');
          emit('');
        }
        onKeyDown?.(e);
      }}
      trailing={
        text ? (
          <button
            type="button"
            className="input-action"
            aria-label="ล้างคำค้นหา"
            onClick={() => {
              update('');
              emit('');
            }}
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : undefined
      }
    />
  );
}

export interface MoneyInputProps extends Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'type' | 'leading' | 'icon'> {
  /** Integer minor units (satang); null when empty or invalid. */
  valueMinor: number | null;
  onChangeMinor: (minor: number | null) => void;
  allowNegative?: boolean;
}

function minorToText(minor: number | null): string {
  return minor == null ? '' : formatMinor(minor, 'THB', { symbol: false });
}

/** Staff type baht ("1,500.50"); the component emits integer satang via shared `bahtToMinor`. */
export function MoneyInput({ valueMinor, onChangeMinor, allowNegative = false, error, onBlur, ...rest }: MoneyInputProps) {
  const [text, setText] = useState(() => minorToText(valueMinor));
  const [parseError, setParseError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const lastEmitted = useRef<number | null>(valueMinor);

  useEffect(() => {
    if (valueMinor !== lastEmitted.current) {
      lastEmitted.current = valueMinor;
      setText(minorToText(valueMinor));
      setParseError(null);
    }
  }, [valueMinor]);

  const parse = (raw: string): { minor: number | null; error: string | null } => {
    const trimmed = raw.trim();
    if (!trimmed) return { minor: null, error: null };
    try {
      const minor = bahtToMinor(trimmed);
      if (!allowNegative && minor < 0) return { minor: null, error: 'จำนวนเงินต้องไม่ติดลบ' };
      return { minor, error: null };
    } catch {
      return { minor: null, error: 'จำนวนเงินไม่ถูกต้อง (ทศนิยมไม่เกิน 2 ตำแหน่ง)' };
    }
  };

  const emit = (minor: number | null) => {
    lastEmitted.current = minor;
    onChangeMinor(minor);
  };

  return (
    <TextInput
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      leading="฿"
      value={text}
      error={error ?? (touched ? parseError : null)}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const result = parse(raw);
        setParseError(result.error);
        emit(result.minor);
      }}
      onBlur={(e) => {
        setTouched(true);
        const result = parse(text);
        if (!result.error && result.minor !== null) setText(minorToText(result.minor));
        onBlur?.(e);
      }}
    />
  );
}
